import React from "react";
import { Group, Stack, Text } from "@mantine/core";
import { Badge, Button, EmptyState, SectionCard, spacePx } from "@xplatform/design-system";
import { IconEdit, IconKey, IconPlus, IconShieldCheck, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ProviderAdapter, ProviderCredential } from "@models/providerCatalog";
import { AdminTable, col } from "@components/AdminTable";

interface ProviderSectionProps {
  provider: ProviderAdapter;
  canManage: boolean;
  onAdd: (provider: ProviderAdapter) => void;
  onEdit: (provider: ProviderAdapter, credential: ProviderCredential) => void;
  onDelete: (provider: ProviderAdapter, credential: ProviderCredential) => void;
}

/** One runtime provider: what it is, how it authenticates, and its credentials with the modules they serve. */
const ProviderSection: React.FC<ProviderSectionProps> = ({ provider, canManage, onAdd, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const credentials = provider.credentials ?? [];

  const columns = [
    col.text<ProviderCredential>("name", t("admin.providers.columns.name"), (c) => c.name),
    col.badge<ProviderCredential>("modules", t("admin.providers.columns.modules"), (c) => (
      <Group gap={4}>
        {c.modules.map((m) => (
          <Badge key={m.id} variant="light" color={m.name === null ? "orange" : "blue"}
                 title={m.name === null ? t("admin.providers.moduleGone") : m.id}>
            {m.name ?? m.id}
          </Badge>
        ))}
      </Group>
    ), "28%"),
    ...(provider.endpoint.use !== "unused"
      ? [col.text<ProviderCredential>("endpoint", t("admin.providers.columns.endpoint"), (c) => c.endpoint, { mono: true, width: "28%" })]
      : []),
    ...(provider.api_version.use !== "unused"
      ? [col.text<ProviderCredential>("version", t("admin.providers.columns.apiVersion"), (c) => c.api_version, { width: 160 })]
      : []),
    ...(provider.api_key.use !== "unused"
      ? [col.badge<ProviderCredential>("key", t("admin.providers.columns.apiKey"), (c) => (
          <Badge color={c.has_api_key ? "green" : "red"} variant="light">
            {t(c.has_api_key ? "admin.providers.keySet" : "admin.providers.keyMissing")}
          </Badge>
        ))]
      : []),
    col.badge<ProviderCredential>("sync", t("admin.providers.columns.sync"), (c) => (
      <Badge color={c.in_sync ? "green" : "orange"} variant="light"
             title={c.in_sync ? undefined : t("admin.providers.outOfSyncHint")}>
        {t(c.in_sync ? "admin.providers.inSync" : "admin.providers.outOfSync")}
      </Badge>
    )),
    col.date<ProviderCredential>("updated", t("admin.providers.columns.updated"), (c) => c.updated_at),
    ...(canManage
      ? [col.actions<ProviderCredential>("", (c) => [
          { label: t("admin.providers.edit"), icon: <IconEdit size={16} />, onClick: () => onEdit(provider, c) },
          { label: t("admin.providers.delete"), icon: <IconTrash size={16} />, color: "red", onClick: () => onDelete(provider, c) },
        ])]
      : []),
  ];

  const title = (
    <Stack gap={spacePx[1]}>
      <Group gap="xs">
        <Text fw={600}>{provider.label}</Text>
        <Badge variant="light" color="gray">{provider.key}</Badge>
        {!provider.supported && <Badge color="orange" variant="light">{t("admin.providers.unsupported")}</Badge>}
      </Group>
      <Group gap="xs">
        <Badge variant="outline" leftSection={provider.auth_mode === "iam" ? <IconShieldCheck size={12} /> : <IconKey size={12} />}>
          {t(`admin.providers.auth.${provider.auth_mode}`)}
        </Badge>
        {provider.capabilities.map((capability) => (
          <Badge key={capability} variant="light">{capability}</Badge>
        ))}
        {provider.prefixes.length > 0 && (
          <Text size="xs" c="dimmed">
            {t("admin.providers.prefixes", { prefixes: provider.prefixes.map((p) => `${p}:`).join(", ") })}
          </Text>
        )}
      </Group>
    </Stack>
  );

  return (
    <SectionCard
      title={title}
      actions={canManage && provider.supported ? (
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => onAdd(provider)}>
          {t("admin.providers.addCredential")}
        </Button>
      ) : undefined}
    >
      {provider.credentials === null ? (
        <Text size="sm" c="dimmed">{t("admin.providers.noCredentialAccess")}</Text>
      ) : credentials.length === 0 ? (
        <EmptyState
          title={t(provider.credential_required ? "admin.providers.empty.required" : "admin.providers.empty.optional")}
          description={t(provider.credential_required ? "admin.providers.empty.requiredHint" : "admin.providers.empty.optionalHint")}
        />
      ) : (
        <Stack gap="xs">
          <AdminTable columns={columns} data={credentials} getRowKey={(c) => c.id} />
          <Text size="xs" c="dimmed">{t("admin.providers.onlyGrantedModules")}</Text>
        </Stack>
      )}
    </SectionCard>
  );
};

export default ProviderSection;
